import type { ImgHTMLAttributes } from "react";

/**
 * Responsive `<picture>` wrapper for images living in `/public/media`.
 *
 * Build-time variants exist for every raster in that folder as
 * `<name>-{640,1024,1600}.{avif,webp}`. This component serves AVIF first,
 * then WebP, then the original file as a fallback, so browsers only download
 * the smallest format and width they actually need.
 */

const WIDTHS = [640, 1024, 1600] as const;

/** `/media/foo.jpg` -> `/media/foo` (variant basename). */
function variantBase(src: string) {
  const dot = src.lastIndexOf(".");
  return dot === -1 ? src : src.slice(0, dot);
}

function srcSet(src: string, ext: "avif" | "webp", widths: readonly number[]) {
  const base = variantBase(src);
  return widths.map((w) => `${base}-${w}.${ext} ${w}w`).join(", ");
}

export type PictureProps = ImgHTMLAttributes<HTMLImageElement> & {
  /** Original path under `/media`, e.g. `/media/loli-slider-1.jpg`. */
  src: string;
  alt: string;
  /** `sizes` attribute; defaults to full viewport width. */
  sizes?: string;
  /** Widths to advertise. Narrow this for small thumbnails. */
  widths?: readonly number[];
  /** Class applied to the `<picture>` element (rare; usually style the img). */
  pictureClassName?: string;
};

export function Picture({
  src,
  alt,
  sizes = "100vw",
  widths = WIDTHS,
  pictureClassName,
  ...img
}: PictureProps) {
  const variantWidths = widths.length ? widths : WIDTHS;

  // Variants only exist for files bundled under /public/media. Anything else
  // (CDN assets, remote URLs) renders as a plain image.
  if (!src.startsWith("/media/")) {
    return <img src={src} alt={alt} {...img} />;
  }

  return (
    <picture className={pictureClassName}>
      <source type="image/avif" srcSet={srcSet(src, "avif", variantWidths)} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet(src, "webp", variantWidths)} sizes={sizes} />
      <img src={src} alt={alt} sizes={sizes} {...img} />
    </picture>
  );
}

/** Best variant URL for a given width — used for `<link rel="preload">`. */
export function preloadHref(src: string, width: 640 | 1024 | 1600 = 1600) {
  return `${variantBase(src)}-${width}.avif`;
}
